import { z } from "zod";
import { responseSchema } from "../http.js";
/**
 * ═══════════════════════════════════════════════════════════
 * Bitácora — quién pidió qué, y qué se le contestó
 *
 * Anahí, 2-oct-2026: «una bitácora con sus peticiones, quién realizó su
 * petición y la respuesta de dicha petición», como la de Matrix, con
 * correo cuando algo se rompe o una IP entra a la lista negra.
 *
 * Qué se guarda: toda petición que CAMBIA algo (POST/PUT/PATCH/DELETE),
 * todo error (4xx/5xx, también de lectura) y los reventones del
 * navegador. Las lecturas exitosas no: serían miles de renglones sin
 * señal. El cuerpo de la petición y de la respuesta se guardan PASADOS
 * POR UN FILTRO —contraseñas, tokens y archivos nunca llegan a la base;
 * cuentas bancarias, solo los últimos 4 dígitos—.
 * ═══════════════════════════════════════════════════════════
 */
export const AUDIT_KINDS = ["request", "client"];
export const auditKindSchema = z.enum(AUDIT_KINDS);
export const auditEntrySchema = z.object({
    id: z.string(),
    at: z.iso.datetime(),
    /** `request` = una petición al API; `client` = un error del navegador. */
    kind: auditKindSchema,
    requestId: z.string().optional(),
    /** Quién: ausente si la petición fue anónima. */
    actorId: z.string().optional(),
    actorEmail: z.string().optional(),
    actorRole: z.string().optional(),
    /**
     * El correo SOBRE el que se actuó cuando no hay sesión: el del login,
     * el de «olvidé mi contraseña», el del registro.
     */
    subjectEmail: z.string().optional(),
    /** Qué, en palabras: «Inicio de sesión», «Cambio de rol»… */
    label: z.string(),
    method: z.string(),
    path: z.string(),
    status: z.number().int(),
    outcome: z.enum(["success", "error"]),
    durationMs: z.number().int().optional(),
    ip: z.string().optional(),
    country: z.string().optional(),
    userAgent: z.string().optional(),
    /** Lo que se pidió, ya filtrado. */
    request: z.unknown().optional(),
    /** Lo que se contestó, ya filtrado. */
    response: z.unknown().optional(),
    errorCode: z.string().optional(),
    errorMessage: z.string().optional(),
    /** Solo los errores del navegador: la pila y la pantalla donde pasó. */
    stack: z.string().optional(),
    page: z.string().optional(),
});
export const auditQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(50),
    /** Busca en correo, ruta, IP y acción. */
    q: z.string().trim().max(200).optional(),
    kind: auditKindSchema.optional(),
    outcome: z.enum(["success", "error"]).optional(),
    statusClass: z.enum(["2xx", "3xx", "4xx", "5xx"]).optional(),
    method: z.enum(["GET", "POST", "PUT", "PATCH", "DELETE"]).optional(),
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
});
export const auditListSchema = z.object({
    items: z.array(auditEntrySchema),
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    perPage: z.number().int().min(1),
});
// ── Errores agrupados: «¿qué se está rompiendo y a quién?» ──
export const auditErrorSummaryQuerySchema = z.object({
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
});
export const auditErrorGroupSchema = z.object({
    kind: auditKindSchema,
    method: z.string(),
    path: z.string(),
    status: z.number().int(),
    errorCode: z.string().optional(),
    errorMessage: z.string().optional(),
    count: z.number().int(),
    users: z.array(z.string()),
    lastAt: z.iso.datetime(),
});
export const auditErrorSummarySchema = z.object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    total: z.number().int(),
    affected: z.number().int(),
    groups: z.array(auditErrorGroupSchema),
});
// ── Lista negra ──────────────────────────────────────────
export const securityBlockSchema = z.object({
    id: z.string(),
    ip: z.string(),
    at: z.iso.datetime(),
    blockedUntil: z.iso.datetime(),
    /** Sigue vetada en este momento. */
    active: z.boolean(),
    points: z.number(),
    offenses: z.number(),
    reason: z.string(),
    /** Ya la habían vetado en los últimos 7 días: el veto dura 24 h. */
    repeat: z.boolean(),
    /** Probó rutas propias de It's The Best: no es un escaneo genérico. */
    targeted: z.boolean(),
    paths: z.array(z.string()),
    userAgents: z.array(z.string()),
    statuses: z.array(z.number()),
    liftedAt: z.iso.datetime().optional(),
});
export const securityBlockQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(25),
    q: z.string().trim().max(64).optional(),
    status: z.enum(["active", "expired"]).optional(),
});
export const securityBlockListSchema = z.object({
    items: z.array(securityBlockSchema),
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    perPage: z.number().int().min(1),
});
export const auditListResponseSchema = responseSchema(auditListSchema);
export const auditErrorSummaryResponseSchema = responseSchema(auditErrorSummarySchema);
export const securityBlockListResponseSchema = responseSchema(securityBlockListSchema);
export const securityBlockLiftResponseSchema = responseSchema(z.object({ lifted: z.literal(true) }));
export const AUDIT_ROUTES = {
    list: "/api/admin/audit",
    errorSummary: "/api/admin/audit/errors-summary",
    blocks: "/api/admin/audit/blocks",
    /** DELETE: levanta el veto antes de tiempo (un cliente real que cayó). */
    block: (id) => `/api/admin/audit/blocks/${id}`,
};
//# sourceMappingURL=audit.js.map